import { Component, type ErrorInfo, type ReactNode } from "react";
import posthog, { isPostHogConfigured } from "./posthog";
import type { EventName, EventProps } from "./events";
import type { AuthUser } from "./authClient";
import { isFirebaseConfigured } from "./firebase";
import { symbolicate } from "./symbolicate";
import {
  initMetaPixel,
  makeMetaEventId,
  metaCapiSend,
  metaPixelTrack,
  type MetaStandardEvent,
} from "./metaPixel";
import { recordSessionEvent } from "./sessionLog";

/**
 * Mapping from our typed `EventName` registry to a Meta Pixel event.
 *
 * `track()` forwards to GA4 unconditionally; any entry that also lives
 * in this table additionally fires a Pixel event under the mapped name.
 * Keep it small — only events that map cleanly to Meta's funnel get an
 * entry; the rest stay GA4-only.
 *
 * Standard names (PageView, ViewContent, Lead, CompleteRegistration,
 * InitiateCheckout, Subscribe, Purchase, StartTrial) hit Meta's
 * optimization path; anything else goes through `trackCustom` and is
 * reportable but not optimizable.
 */
export const META_EVENT_MAP: Partial<Record<EventName, MetaStandardEvent | string>> = {
  // First-touch ad attribution beat.
  campaign_landed: "CampaignLanded",
  // First furniture piece placed = activation. ViewContent is the
  // standard event Meta documents for "user engaged with key content".
  furniture_first_placed: "ViewContent",
  // The AI ad's promise delivered on the landing (reportable, not optimized).
  feng_shui_example_applied: "FengShuiExampleApplied",
  // The visitor's own room exists (template, sketch or shape).
  room_created: "RoomDrawn",
  // A real AI layout on the visitor's room; the example never fires this.
  ai_layout_applied: "AiLayoutApplied",
  // The 3D ad's promise delivered: the showcase room's first 3D frame. The
  // 3D ad set optimises for a custom conversion on it.
  render_3d_example_ready: "Render3DExampleViewed",
  // A new account = the registration conversion.
  sign_up: "CompleteRegistration",
  begin_checkout: "InitiateCheckout",
  // Subscribe / Purchase are sent server-side from the Stripe
  // subscription trigger (functions/src/analytics/subscriptionEvents.ts),
  // which knows the real value and can't be lost to a closed tab.
};

/**
 * Meta events that depend on the props: fired only when the rule returns a
 * name. Checked after {@link META_EVENT_MAP}.
 */
export const META_CONDITIONAL_EVENTS: Partial<
  Record<EventName, (props: Record<string, unknown> | undefined) => string | null>
> = {
  // The visitor's own room in 3D (the 3D first run's activation), not a starter room.
  render_3d_opened: (props) => (props?.own_room === true ? "Render3DOwnRoom" : null),
};

/**
 * Single-vendor-agnostic facade over the Google Cloud monitoring
 * suite. All app code imports from this module — NEVER import
 * `firebase/analytics` or any GCP error-reporting client directly
 * outside of this file.
 *
 * That keeps:
 *
 *   - Unit tests vendor-free (no env vars / Firebase ⇒ all functions
 *     become safe no-ops, same pattern as
 *     {@link import("./firebase").isFirebaseConfigured}).
 *   - Migration cheap (the previous Sentry + PostHog backend lived
 *     entirely inside this file; swapping to another vendor would
 *     touch only this module).
 *   - Call sites uncluttered with vendor-specific guards.
 *
 * Backends:
 *   - **Errors** → Cloud Error Reporting via the public REST endpoint
 *     `clouderrorreporting.googleapis.com/v1beta1/.../events:report`.
 *     The endpoint takes a Google Cloud API key (separate from the
 *     Firebase web key) that's restricted to Error Reporting + HTTP
 *     referrer. Stack traces are symbolicated on the client via
 *     {@link import("./symbolicate").symbolicate} so prod issues
 *     show readable file names + line numbers.
 *   - **Analytics** → Firebase Analytics (GA4). `track()` becomes
 *     `logEvent(analytics, name, props)`. Requires the Firebase
 *     config to include a `measurementId` (set via
 *     `VITE_FIREBASE_MEASUREMENT_ID`).
 *   - **Breadcrumbs** → in-memory ring buffer flushed onto each
 *     error report's `message` field. The Error Reporting UI doesn't
 *     have a dedicated breadcrumb panel like Sentry, so we
 *     concatenate the last 50 events under a divider so they
 *     appear in the issue body.
 *
 * Init contract:
 *   - {@link initTelemetry} is called exactly once from `main.tsx`
 *     before the first React render.
 *   - Errors and analytics initialize independently. Either, both,
 *     or neither may be active. Missing env vars = silent no-op.
 *   - {@link TelemetryErrorBoundary} works even when no backends
 *     are configured — it still catches React errors and renders
 *     the fallback, just doesn't report them.
 */

type EventPayload<K extends EventName> = K extends keyof EventProps
  ? EventProps[K]
  : Record<string, never>;

/** Wire shape for the Cloud Error Reporting events:report API. */
interface ErrorEvent {
  eventTime: string;
  serviceContext: {
    service: string;
    version?: string;
  };
  message: string;
  context: {
    user?: string;
    httpRequest?: {
      method: string;
      url: string;
      userAgent: string;
      referrer: string;
    };
  };
}

interface Breadcrumb {
  message: string;
  level: "info" | "warning" | "error";
  data?: Record<string, unknown>;
  timestamp: number;
}

const BREADCRUMB_BUFFER_SIZE = 50;
const SERVICE_NAME = "room-canvas-web";

let errorsReady = false;
/** `gtag` once the Google tag is on the page. `off` when no measurement id. */
let analyticsMode: "off" | "gtag" = "off";
let gaMeasurementId: string | undefined;
let initialized = false;

let gcpProjectId: string | undefined;
let gcpApiKey: string | undefined;
let release: string | undefined;

/** Identified user id — stamped onto every Error Reporting POST so
 *  issues are grouped/searchable by user. Cleared on sign-out via
 *  {@link identify}. Lifecycle owned by `telemetryAuthBridge`. */
let currentUid: string | null = null;

const breadcrumbs: Breadcrumb[] = [];

function readEnv(name: string): string | undefined {
  const v = import.meta.env[name as keyof ImportMetaEnv] as string | undefined;
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * One console warning when Firebase itself is configured but the GA4
 * measurement id was never set. Without it, getAnalytics throws and
 * every track() call silently stays in the breadcrumb buffer.
 */
export function warnIfAnalyticsDisconnected(
  firebaseConfigured: boolean,
  measurementId: string | undefined
): void {
  if (!firebaseConfigured || measurementId) return;
  console.warn(
    "[telemetry] Firebase is configured but VITE_FIREBASE_MEASUREMENT_ID is empty, so track() events will not reach Google Analytics."
  );
}

/**
 * Use the page's one Google tag. `getAnalytics` reads measurementId from
 * Firebase webConfig, which stays empty after a late Analytics link and
 * then drops every event. Commands must go through `window.gtag` (an
 * `arguments` object). Pushing a plain array onto `dataLayer` after
 * gtag.js has booted does not send a hit.
 */
export function ensureGoogleTag(measurementId: string): void {
  if (typeof document === "undefined") return;
  const w = window as Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer ?? [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtag() {
      w.dataLayer!.push(arguments);
    } as (...args: unknown[]) => void;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
    w.gtag("js", new Date());
    w.gtag("config", measurementId);
  }
}

function gtag(...args: unknown[]): void {
  const fn = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof fn === "function") fn(...args);
}

/**
 * Initialize the configured backends. Idempotent. Failure to init
 * either backend is non-fatal — we log to the console and leave that
 * backend's facade calls as no-ops so the rest of the app keeps
 * running.
 */
export function initTelemetry(): void {
  if (initialized) return;
  initialized = true;

  gcpProjectId = readEnv("VITE_GCP_PROJECT_ID");
  gcpApiKey = readEnv("VITE_GCP_ERROR_REPORTING_KEY");
  release = readEnv("VITE_RELEASE");

  // Errors only need the project id + API key. Either being missing
  // is the contributor-without-prod-keys case — no-op silently.
  errorsReady = Boolean(gcpProjectId && gcpApiKey);

  // Boot the Meta Pixel side-by-side with GA4. The Pixel module is its
  // own no-op when `VITE_META_PIXEL_ID` is unset, so contributor builds
  // without ad infrastructure are unaffected.
  initMetaPixel();

  // Analytics piggybacks on the Firebase app — measurementId presence
  // (configured via VITE_FIREBASE_MEASUREMENT_ID, see firebase.ts) is
  // what the Firebase SDK uses to decide whether GA4 can boot.
  // An empty id used to fail inside getAnalytics and get swallowed,
  // which is why production shipped with no GA4 property and no data.
  const measurementId = readEnv("VITE_FIREBASE_MEASUREMENT_ID");
  warnIfAnalyticsDisconnected(isFirebaseConfigured(), measurementId);
  if (measurementId) {
    ensureGoogleTag(measurementId);
    analyticsMode = "gtag";
    gaMeasurementId = measurementId;
  }
}

/**
 * The GA4 `client_id` for this browser, or null when analytics is off or
 * the tag doesn't answer within `timeoutMs`. Server-side Measurement
 * Protocol hits need it to land in the same GA4 user timeline.
 */
export function getAnalyticsClientId(timeoutMs = 3000): Promise<string | null> {
  if (analyticsMode !== "gtag" || !gaMeasurementId) return Promise.resolve(null);
  const id = gaMeasurementId;
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    gtag("get", id, "client_id", (clientId: unknown) => {
      clearTimeout(timer);
      resolve(typeof clientId === "string" && clientId.length > 0 ? clientId : null);
    });
  });
}

function sendAnalyticsUser(
  uid: string | null,
  extras?: Record<string, unknown>
): void {
  if (analyticsMode !== "gtag") return;
  // `set` updates the user without sending another page_view. A second
  // `config` cancels the in-flight page view.
  gtag("set", {
    user_id: uid,
    ...(extras ? { user_properties: extras } : {}),
  });
}

/**
 * Set a single GA4 user property without touching `user_id`. Works for
 * signed-out visitors too — used for device-level facts like
 * `in_app_browser` or which first run a visitor landed in, so GA4
 * Explorations can segment cohorts by them.
 */
export function setUserProperty(name: string, value: string | boolean | number | null): void {
  pushBreadcrumb({
    message: `user_property:${name}`,
    level: "info",
    data: { value },
    timestamp: Date.now(),
  });
  if (analyticsMode !== "gtag") return;
  gtag("set", { user_properties: { [name]: value } });
}

function sendAnalyticsEvent(name: string, params: Record<string, unknown>): void {
  if (analyticsMode !== "gtag") return;
  gtag("event", name, params);
}

/**
 * Stamp / clear the identified user on both backends.
 *
 * For Firebase Analytics, we use the uid as the GA4 User-ID. For
 * Error Reporting, the uid is cached so subsequent error POSTs
 * include it in the `context.user` field.
 *
 * `extras` is forwarded to Analytics as user properties (the
 * `is_premium` flag is the canonical one). Avoid email / displayName
 * here unless you've consented to that PII path.
 */
export function identify(
  user: AuthUser | null,
  extras?: Record<string, unknown>
): void {
  if (!user) {
    currentUid = null;
    sendAnalyticsUser(null);
    return;
  }
  currentUid = user.uid;
  sendAnalyticsUser(user.uid, extras);
}

/**
 * Track a product event. Names + property shapes are type-checked
 * against {@link import("./events").EventProps}; adding a new event
 * means updating that registry so the call site type-checks.
 *
 * GA4 caps event names at 40 chars and 25 props per event — our
 * registry stays well under both. Names use `snake_case` so the GA4
 * UI groups them sensibly.
 *
 * Errors thrown by the underlying SDK are swallowed — analytics
 * failures should never break the app.
 */
type TrackListener = (name: EventName, props: Record<string, unknown> | undefined) => void;
const trackListeners = new Set<TrackListener>();

/** High-value browser actions forwarded to PostHog from their existing action-time telemetry calls. */
const POSTHOG_CAPTURE_EVENTS: ReadonlySet<EventName> = new Set([
  "room_created",
  "room_saved",
  "sign_up",
  "login",
  "ai_layout_requested",
  "ai_layout_succeeded",
  "ai_product_import_requested",
  "ai_product_import_succeeded",
  "product_model_official_result",
  "product_model_reconstruct_result",
  "product_model_shown",
  "product_model_show_failed",
  "upsell_upgrade_clicked",
  "begin_checkout",
  "premium_activated",
  "share_invite_sent",
  "share_invite_accepted",
  "render_3d_opened",
  "render_3d_photo_saved",
  "finishes_opened",
  "finish_changed",
  "look_applied",
  "finish_surface_tapped",
  "finishes_closed",
]);

/**
 * Purpose-written workflow records for PostHog Logs. This dedicated bridge
 * deliberately omits action props so user input and existing app logs never
 * leave the browser through the log exporter.
 */
const POSTHOG_WORKFLOW_LOGS: ReadonlyMap<
  EventName,
  { message: string; workflow: string }
> = new Map([
  ["room_created", { message: "room creation completed", workflow: "room_creation" }],
  [
    "ai_layout_requested",
    { message: "AI layout generation requested", workflow: "ai_layout" },
  ],
  ["begin_checkout", { message: "checkout started", workflow: "checkout" }],
]);

function logPostHogWorkflow(name: EventName): void {
  const record = POSTHOG_WORKFLOW_LOGS.get(name);
  if (!record) return;
  posthog.logger.info(record.message, { workflow: record.workflow });
}

/** Remove opaque room ids and user-controlled first-touch parameters before capture. */
function posthogProperties(
  name: EventName,
  props: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!props) return undefined;
  switch (name) {
    case "room_saved":
      return { is_new: props.is_new };
    case "sign_up":
      return { method: props.method, surface: props.surface };
    case "share_invite_sent":
    case "share_invite_accepted":
      return { kind: props.kind };
    default:
      return props;
  }
}

/**
 * Observe product events in-process. Lets small UI features (the
 * first-run checklist, for one) react to "the user did X" without the
 * emitting code knowing about them. Listeners must never throw; a
 * failure here is swallowed so analytics is never the thing that breaks
 * the app.
 */
export function subscribeTrackedEvents(listener: TrackListener): () => void {
  trackListeners.add(listener);
  return () => {
    trackListeners.delete(listener);
  };
}

export function track<K extends EventName>(
  name: K,
  ...args: EventPayload<K> extends Record<string, never> ? [] : [props: EventPayload<K>]
): void {
  const props = args[0] as Record<string, unknown> | undefined;
  for (const l of trackListeners) {
    try {
      l(name, props);
    } catch {
      // never let an observer break tracking
    }
  }
  // Always record a breadcrumb regardless of analytics state — that
  // way crash reports include the full event timeline even when the
  // user is offline-only / analytics is disabled.
  pushBreadcrumb({
    message: `track:${name}`,
    level: "info",
    data: props,
    timestamp: Date.now(),
  });

  // The Firebase Auth bridge has already established the browser identity,
  // so these captures inherit it without passing identity per action.
  if (isPostHogConfigured && POSTHOG_CAPTURE_EVENTS.has(name)) {
    try {
      posthog.capture(name, posthogProperties(name, props));
      logPostHogWorkflow(name);
    } catch {
      // analytics failures must never affect the user action
    }
  }

  // Session id is attached to the GA4 hit below. Firestore persistence
  // no-ops until initSessionLog() and only for a signed-in uid.
  let sessionId: string | undefined;
  try {
    sessionId = recordSessionEvent(name, props, currentUid);
  } catch {
    // a session-log bug must not drop the analytics event
  }

  // Meta Pixel + CAPI: fire-and-forget. The Pixel module is a no-op
  // when `VITE_META_PIXEL_ID` is unset. CAPI only runs for signed-in
  // users (`identify` stamps `currentUid`) because `metaCapiTrack` is
  // an authed callable. Both sides share one `event_id` so Meta
  // counts a dual-fired conversion once.
  const metaEvent = META_EVENT_MAP[name] ?? META_CONDITIONAL_EVENTS[name]?.(props) ?? null;
  if (metaEvent) {
    const eventId = makeMetaEventId();
    const metaProps = props as Record<string, unknown> | undefined;
    metaPixelTrack(metaEvent, metaProps, eventId);
    if (currentUid) {
      void metaCapiSend(metaEvent, eventId, metaProps);
    }
  }

  if (analyticsMode === "off") return;
  sendAnalyticsEvent(name, {
    ...(props ?? {}),
    ...(sessionId ? { session_id: sessionId } : {}),
  });
}

/**
 * Report a thrown value to Cloud Error Reporting. Use this for caught
 * exceptions that we're handling locally but still want to know about
 * (e.g. background sync failures, soft fallbacks). Render errors are
 * captured automatically by {@link TelemetryErrorBoundary}, and the
 * global `unhandledrejection` / `error` listeners installed in
 * {@link initTelemetry} would cover the rest if we ever add them.
 */
export function captureException(
  err: unknown,
  ctx?: Record<string, unknown>
): void {
  if (!errorsReady) return;
  // Fire-and-forget — `void` so callers don't need to `await`. Any
  // failure inside the POST swallows the rejection so a failing
  // telemetry blip never breaks user code.
  void reportToCloud(err, ctx).catch(() => {});
}

/**
 * Add a breadcrumb to the in-memory ring buffer. These are appended
 * to the next error report's `message` field under a divider — the
 * closest GCP equivalent of Sentry's breadcrumb panel. Use sparingly:
 * the ring is small (50 entries) so spamming pushes useful older
 * entries out.
 */
export function addBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level: "info" | "warning" | "error" = "info"
): void {
  pushBreadcrumb({ message, data, level, timestamp: Date.now() });
}

function pushBreadcrumb(crumb: Breadcrumb): void {
  breadcrumbs.push(crumb);
  if (breadcrumbs.length > BREADCRUMB_BUFFER_SIZE) {
    // Drop the oldest. Array shift is O(n) but 50 entries is
    // negligible and keeps the buffer's order simple to reason about.
    breadcrumbs.shift();
  }
}

/** True iff at least one backend is configured. */
export function isTelemetryConfigured(): boolean {
  return errorsReady || analyticsMode === "gtag";
}

async function reportToCloud(
  err: unknown,
  ctx?: Record<string, unknown>
): Promise<void> {
  if (!gcpProjectId || !gcpApiKey) return;
  const message = await buildReportMessage(err, ctx);
  const event: ErrorEvent = {
    eventTime: new Date().toISOString(),
    serviceContext: {
      service: SERVICE_NAME,
      version: release,
    },
    message,
    context: {
      ...(currentUid ? { user: currentUid } : {}),
      ...buildHttpContext(),
    },
  };
  const url =
    `https://clouderrorreporting.googleapis.com/v1beta1/projects/` +
    `${encodeURIComponent(gcpProjectId)}/events:report?key=` +
    encodeURIComponent(gcpApiKey);
  try {
    await fetch(url, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
      // `keepalive` so an error during pagehide / before-unload still
      // makes it out the door. Browsers cap keepalive bodies at 64 KB,
      // which is far larger than any realistic stack + breadcrumb.
      keepalive: true,
    });
  } catch {
    // network unreachable — drop the event silently
  }
}

/**
 * Compose the full `message` field for an Error Reporting event:
 * symbolicated stack trace + appended context + breadcrumb timeline.
 *
 * Cloud Error Reporting parses the V8 stack format out of `message`
 * to extract the file/line/function. Anything we add ABOVE the
 * `Error: …` prefix would confuse the parser, so contextual
 * additions go AFTER the stack under clearly-marked dividers.
 */
async function buildReportMessage(
  err: unknown,
  ctx?: Record<string, unknown>
): Promise<string> {
  let stack: string;
  let header: string;
  if (err instanceof Error) {
    header = `${err.name || "Error"}: ${err.message || "(no message)"}`;
    const rawStack = err.stack ?? `${header}\n    at <no stack>`;
    stack = await symbolicate(rawStack);
  } else {
    // Non-Error throws (a string, an object, undefined) — synthesize
    // a stack so Error Reporting's parser doesn't reject the event.
    header = `NonErrorThrown: ${safeString(err)}`;
    stack = `${header}\n    at captureException (telemetry.ts:0:0)`;
  }

  const parts: string[] = [stack];

  if (ctx && Object.keys(ctx).length > 0) {
    parts.push("");
    parts.push("--- context ---");
    parts.push(safeJson(ctx));
  }

  if (breadcrumbs.length > 0) {
    parts.push("");
    parts.push("--- breadcrumbs (newest last) ---");
    // Snapshot the buffer so concurrent pushes don't reorder this
    // particular event's timeline.
    const snapshot = breadcrumbs.slice();
    for (const c of snapshot) {
      const ts = new Date(c.timestamp).toISOString();
      const data = c.data ? ` ${safeJson(c.data)}` : "";
      parts.push(`[${c.level}] ${ts} ${c.message}${data}`);
    }
  }

  return parts.join("\n");
}

function buildHttpContext():
  | { httpRequest: ErrorEvent["context"]["httpRequest"] }
  | Record<string, never> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {};
  }
  return {
    httpRequest: {
      method: "GET",
      url: window.location.href,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
    },
  };
}

function safeString(v: unknown): string {
  try {
    if (v === null) return "null";
    if (v === undefined) return "undefined";
    if (typeof v === "string") return v;
    return JSON.stringify(v);
  } catch {
    return Object.prototype.toString.call(v);
  }
}

function safeJson(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return "[unserializable]";
  }
}

/**
 * React Error Boundary that reports caught render errors via
 * {@link captureException} and falls back to a user-supplied
 * `fallback` node. Maintains parity with the old
 * `Sentry.ErrorBoundary` props ([src/App.tsx](src/App.tsx) is
 * intentionally untouched by the GCP migration).
 *
 * Stays a class component because React still has no hooks-based
 * Error Boundary primitive — `componentDidCatch` is the only API.
 */
interface ErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class TelemetryErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, {
      context: "react.errorBoundary",
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/** Test-only: wipe everything so tests start clean. */
export function _resetTelemetryForTests(): void {
  analyticsMode = "off";
  gaMeasurementId = undefined;
  errorsReady = false;
  initialized = false;
  gcpProjectId = undefined;
  gcpApiKey = undefined;
  release = undefined;
  currentUid = null;
  breadcrumbs.length = 0;
  trackListeners.clear();
}
